import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Fuel, Calendar, Gauge, Settings, Shield, Check, ChevronLeft, Send, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const CarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    start_date: '',
    end_date: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchCarDetails();
  }, [id]);

  const fetchCarDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/cars/${id}`);
      setCar(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching car details:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_BASE_URL}/api/inquiry/car`, {
        ...formData,
        car_name: car?.name
      });
      setSubmitted(true);
      setShowInquiryForm(false);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('There was an error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriceDisplay = () => {
    if (!car) return '';
    // Handle numeric price formatting safely
    if (car.min_price && car.max_price) {
      let minDisplay, maxDisplay;
      try {
        minDisplay = (typeof car.min_price === 'number') 
          ? car.min_price.toLocaleString() 
          : parseFloat(car.min_price).toLocaleString();
        maxDisplay = (typeof car.max_price === 'number') 
          ? car.max_price.toLocaleString() 
          : parseFloat(car.max_price).toLocaleString();
      } catch (e) {
        minDisplay = car.min_price;
        maxDisplay = car.max_price;
      }
      return `${minDisplay} - ${maxDisplay} RWF`;
    } else if (car.base_price) {
      try {
        const numeric = (typeof car.base_price === 'number') 
          ? car.base_price 
          : parseFloat(car.base_price);
        return `${numeric.toLocaleString()} RWF`;
      } catch (e) {
        return car.base_price;
      }
    }
    return 'Price on request';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-dark-800">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-white">Car not found</h2>
        <button onClick={() => navigate('/cars')} className="mt-4 text-orange-500 hover:underline">
          Back to Cars
        </button>
      </div>
    );
  }

  return (
    <div className="bg-dark-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        {/* Back button */}
        <button 
          onClick={() => navigate('/cars')}
          className="flex items-center text-orange-500 hover:text-orange-400 mb-6 transition"
        >
          <ChevronLeft size={20} />
          <span>Back to All Vehicles</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card rounded-xl overflow-hidden"
          >
            <img src={car.image_url} alt={car.name} className="w-full h-auto object-cover" />
          </motion.div>

          {/* Details Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{car.name}</h1>
              <p className="text-gray-400 text-lg">{car.brand}</p>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-lg">
              <div className="text-3xl font-bold">{getPriceDisplay()}</div>
              <p className="text-sm opacity-90">per day</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-3 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Users className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Seats</p>
                    <p className="font-semibold text-white">{car.seats} Passengers</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-3 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Settings className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Transmission</p>
                    <p className="font-semibold text-white">{car.transmission}</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-3 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Fuel className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Fuel Type</p>
                    <p className="font-semibold text-white">{car.fuel_type}</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-3 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Gauge className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Mileage</p>
                    <p className="font-semibold text-white">Unlimited</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-white">Features</h3>
              <div className="grid grid-cols-2 gap-2">
                {['GPS Navigation', 'Leather Seats', 'Air Conditioning', 'Bluetooth', 'Backup Camera', 'Cruise Control'].map((feature, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Check size={16} className="text-green-500" />
                    <span className="text-sm text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {!submitted ? (
              !showInquiryForm ? (
                <button
                  onClick={() => setShowInquiryForm(true)}
                  className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition font-semibold text-lg"
                >
                  Inquire Now
                </button>
              ) : (
                <div className="glass-card p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-4 text-white">Send Inquiry</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="date"
                        name="start_date"
                        required
                        value={formData.start_date}
                        onChange={handleInputChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg text-white"
                      />
                      <input
                        type="date"
                        name="end_date"
                        required
                        value={formData.end_date}
                        onChange={handleInputChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg text-white"
                      />
                    </div>
                    <textarea
                      name="message"
                      rows="3"
                      placeholder="Additional Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                    ></textarea>
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
                      >
                        {submitting ? 'Sending...' : 'Send Inquiry'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowInquiryForm(false)}
                        className="flex-1 border border-dark-600 py-2 rounded-lg hover:bg-dark-700 transition text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )
            ) : (
              <div className="bg-green-500/20 border border-green-500 text-green-400 p-6 rounded-lg text-center">
                <Check size={48} className="mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">Inquiry Sent!</h3>
                <p>Thank you for your interest. We will contact you within 24 hours.</p>
                <button
                  onClick={() => navigate('/cars')}
                  className="mt-4 text-orange-500 hover:underline"
                >
                  Browse More Cars →
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CarDetail;